import { useState, useRef, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Sparkles, Music4, ArrowDown, ArrowRight, KeyRound, Layers, ListMusic, GraduationCap, Square, Repeat, CircleDot, HelpCircle, Lock, Check, Loader2, LogIn, LogOut } from "lucide-react";
import { MODES, TRIADS, PROGRESSIONS, PRACTICE_ROUTINES, TRIADS_G, PRACTICE_ROUTINES_G, noteName } from "@/data/modes";
import { KEYS, ADVANCED_KEYS } from "@/lib/theory";
import { Piano } from "@/components/Piano";
import { PracticeView } from "@/components/PracticeView";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { KeyExplorer } from "@/components/KeyExplorer";
import { Quiz } from "@/components/Quiz";
import { Paywall } from "@/components/Paywall";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { playScale, playChord, preloadPiano } from "@/lib/audio";
import { useT, LANGS, modeName, noteLabel, chordLabel, progName, quality } from "@/i18n";
import { useAuth, getToken } from "@/auth";
import { AuthModal, ResetPasswordModal } from "@/components/AuthModal";

const API = process.env.REACT_APP_BACKEND_URL;
const LOCKED_VIEWS = ["practice", "practice2", "transpose", "advanced", "circle", "quiz"];
// The six white-key diatonic chords map to these modes, in order (I ii iii IV V vi).
const DIATONIC_MODES = ["Ionian", "Dorian", "Phrygian", "Lydian", "Mixolydian", "Aeolian"];
// Paywall is ON. Set to false only to open all pages (e.g. for proofreading).
const PAYWALL_ENABLED = true;

const HERO_IMG =
  "https://images.unsplash.com/photo-1552422530-9b41dc72286b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwcGVyc29uJTIwcGxheWluZyUyMHBpYW5vfGVufDB8fHx8MTc4Mzg2OTgzNXww&ixlib=rb-4.1.0&q=85";

const scrollToPiano = () => {
  const el = document.getElementById("learn");
  if (!el) return;
  // Offset for the sticky header so the section heading isn't hidden behind it.
  const header = document.querySelector("header");
  const offset = (header ? header.offsetHeight : 64) + 12;
  const y = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
};

function App() {
  const { t, lang, setLang } = useT();
  const [view, setView] = useState("modes"); // "modes" | "chords"
  const [activeId, setActiveId] = useState("ionian");
  const [chordId, setChordId] = useState("c-major");
  const [playingMidi, setPlayingMidi] = useState(null);
  const active = MODES.find((m) => m.id === activeId);
  const chord = TRIADS.find((c) => c.id === chordId);
  const chordAsMode = { scale: chord.notes, root: chord.root };

  // ----- Pro / payments -----
  const { user, logout, refresh } = useAuth();
  const purchased = !!(user && user.is_pro);
  const isPro = PAYWALL_ENABLED ? purchased : true;
  const [authModal, setAuthModal] = useState({ open: false, mode: "login", sessionId: null });
  const [resetToken, setResetToken] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [payState, setPayState] = useState("idle"); // idle | verifying | success | canceled | error
  const [aboutOpen, setAboutOpen] = useState(false);

  const startCheckout = async (lookupKey) => {
    setLoadingKey(lookupKey);
    // Open a tab synchronously (inside the user gesture) so it isn't popup-blocked and
    // so Stripe loads at top level — it refuses to render inside the preview iframe.
    const checkoutTab = window.open("", "_blank");
    try {
      const tok = getToken();
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ lookup_key: lookupKey, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error("checkout failed");
      const data = await res.json();
      if (!data.checkout_url) throw new Error("no checkout url");
      if (checkoutTab) {
        checkoutTab.location.href = data.checkout_url;
      } else {
        // Popup blocked: fall back to navigating the top window, then the current one.
        try {
          (window.top || window).location.href = data.checkout_url;
        } catch (e) {
          window.location.href = data.checkout_url;
        }
      }
      setLoadingKey(null);
    } catch (e) {
      if (checkoutTab) checkoutTab.close();
      setLoadingKey(null);
      setPayState("error");
    }
  };

  const handlePlayScale = () => {
    setPlayingMidi(null);
    playScale(active.scale, (m) => {
      setPlayingMidi(m);
      setTimeout(() => setPlayingMidi((p) => (p === m ? null : p)), 360);
    });
  };

  const handlePlayChord = () => {
    playChord(chord.notes);
  };

  // Guided-practice routines resolved to chord objects (C = 1st practice, G = 2nd practice).
  const routinesC = PRACTICE_ROUTINES.map((r) => ({
    id: r.id,
    numerals: r.numerals,
    steps: r.steps.map((id) => TRIADS.find((c) => c.id === id)),
  }));
  const routinesG = PRACTICE_ROUTINES_G.map((r) => ({
    id: r.id,
    numerals: r.numerals,
    steps: r.steps.map((id) => TRIADS_G.find((c) => c.id === id)),
  }));

  useEffect(() => {
    preloadPiano();
  }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes("/payment/success")) {
      const sid = new URLSearchParams(window.location.search).get("session_id");
      if (!sid) {
        window.history.replaceState({}, "", "/");
        return;
      }
      setPayState("verifying");
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await fetch(`${API}/api/payments/status/${sid}`);
          const d = await r.json();
          if (d.payment_status === "paid") {
            if (getToken()) {
              // Logged-in buyer: server already granted Pro — refresh account state.
              await refresh();
              setPayState("success");
            } else {
              // Anonymous buyer: prompt them to create an account, linked to this payment.
              setPayState("idle");
              setAuthModal({ open: true, mode: "postpay", sessionId: sid });
            }
            window.history.replaceState({}, "", "/");
            return;
          }
        } catch (e) {}
        if (attempts < 12) {
          setTimeout(poll, 2000);
        } else {
          setPayState("error");
          window.history.replaceState({}, "", "/");
        }
      };
      poll();
    } else if (path.includes("/payment/cancel")) {
      setPayState("canceled");
      window.history.replaceState({}, "", "/");
    } else if (path.includes("/reset-password")) {
      const tok = new URLSearchParams(window.location.search).get("token");
      if (tok) setResetToken(tok);
      window.history.replaceState({}, "", "/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (v) => {
    setView(v);
    setTimeout(scrollToPiano, 60);
  };

  return (
    <div className="App">
      {/* Payment status overlay / banners */}
      {payState === "verifying" && (
        <div className="fixed inset-0 z-[60] bg-foreground/90 backdrop-blur-sm grid place-items-center" data-testid="pay-verifying">
          <div className="text-background text-center px-6">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" />
            <p className="font-display font-extrabold text-2xl">{t("pay.verifying")}</p>
            <p className="text-background/70 mt-1">{t("pay.verifyingSub")}</p>
          </div>
        </div>
      )}
      {payState === "success" && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] bg-accent text-accent-foreground brutal-border brutal-shadow rounded-full px-5 py-3 flex items-center gap-2" data-testid="pay-success">
          <Check className="w-5 h-5" />
          <span className="font-display font-bold">{t("pay.success")}</span>
          <button onClick={() => setPayState("idle")} className="ml-2 font-bold opacity-70 hover:opacity-100">✕</button>
        </div>
      )}
      {payState === "canceled" && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] bg-background text-foreground brutal-border brutal-shadow rounded-full px-5 py-3 flex items-center gap-2" data-testid="pay-canceled">
          <span className="font-display font-bold">{t("pay.canceled")}</span>
          <button onClick={() => setPayState("idle")} className="ml-2 font-bold opacity-70 hover:opacity-100">✕</button>
        </div>
      )}
      {payState === "error" && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] bg-primary text-primary-foreground brutal-border brutal-shadow rounded-full px-5 py-3 flex items-center gap-2" data-testid="pay-error">
          <span className="font-display font-bold">{t("pay.error")}</span>
          <button onClick={() => setPayState("idle")} className="ml-2 font-bold opacity-70 hover:opacity-100">✕</button>
        </div>
      )}
      {/* ===== Header ===== */}
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-md border-b-2 border-foreground">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 min-h-16 py-2 flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-2.5" data-testid="app-logo">
            <img src="/ionara-logo.png" alt="Ionara" className="h-8 sm:h-9 w-auto" />
            {purchased && (
              <span
                data-testid="pro-badge"
                className="ml-1 text-[10px] font-display font-extrabold bg-accent text-accent-foreground rounded-full px-2 py-0.5 tracking-wide"
              >
                PRO
              </span>
            )}
            <div className="flex items-center gap-1.5 ml-2" data-testid="lang-switcher">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  title={l.label}
                  aria-label={l.label}
                  className={`rounded-sm overflow-hidden border-2 transition-all ${
                    lang === l.code
                      ? "border-foreground scale-110"
                      : "border-transparent opacity-50 hover:opacity-100"
                  }`}
                >
                  <img
                    src={`https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/1x1/${l.flag}.svg`}
                    alt={l.label}
                    className="w-7 h-7 object-cover block"
                  />
                </button>
              ))}
            </div>
            <button
              data-testid="about-btn-header"
              onClick={() => setAboutOpen(true)}
              className="ml-1 text-xs sm:text-sm font-display font-bold px-3 py-1.5 rounded-full brutal-border hover:bg-muted transition-colors"
            >
              {t("footer.about")}
            </button>
            {user === false && (
              <div className="ml-1 flex items-center gap-1.5">
                <span className="hidden sm:inline text-xs font-display font-bold text-foreground" data-testid="already-bought-label">
                  {t("auth.alreadyBought")}
                </span>
                <ArrowRight className="hidden sm:block w-4 h-4 text-foreground shrink-0" />
                <button
                  data-testid="login-btn"
                  onClick={() => setAuthModal({ open: true, mode: "login", sessionId: null })}
                  className="text-xs sm:text-sm font-display font-bold px-3 py-1.5 rounded-full brutal-border hover:bg-muted transition-colors flex items-center gap-1.5"
                >
                  <LogIn className="w-3.5 h-3.5" /> {t("auth.login")}
                </button>
              </div>
            )}
            {user && (
              <button
                data-testid="logout-btn"
                onClick={logout}
                title={user.email}
                className="ml-1 text-xs sm:text-sm font-display font-bold px-3 py-1.5 rounded-full brutal-border hover:bg-muted transition-colors flex items-center gap-1.5"
              >
                <LogOut className="w-3.5 h-3.5" /> {t("auth.logout")}
              </button>
            )}
          </div>
          <nav className="flex flex-nowrap items-center gap-1.5 bg-muted brutal-border rounded-full p-1 max-w-full overflow-x-auto w-full sm:w-auto" data-testid="main-nav">
            <button
              data-testid="nav-modes"
              onClick={() => goTo("modes")}
              className={`font-display font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                view === "modes" ? "bg-foreground text-background" : "text-foreground hover:bg-background"
              }`}
            >
              <Layers className="w-4 h-4" /> {t("nav.modes")}
            </button>
            <button
              data-testid="nav-chords"
              onClick={() => goTo("chords")}
              className={`font-display font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                view === "chords" ? "bg-foreground text-background" : "text-foreground hover:bg-background"
              }`}
            >
              <ListMusic className="w-4 h-4" /> {t("nav.chords")}
            </button>
            <button
              data-testid="nav-practice"
              onClick={() => goTo("practice")}
              className={`font-display font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                view === "practice" ? "bg-foreground text-background" : "text-foreground hover:bg-background"
              }`}
            >
              <GraduationCap className="w-4 h-4" /> {t("nav.practice")} {!isPro && <Lock className="w-3 h-3 opacity-60" />}
            </button>
            <button
              data-testid="nav-practice2"
              onClick={() => goTo("practice2")}
              className={`font-display font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                view === "practice2" ? "bg-foreground text-background" : "text-foreground hover:bg-background"
              }`}
            >
              <GraduationCap className="w-4 h-4" /> {t("nav.practice2")} {!isPro && <Lock className="w-3 h-3 opacity-60" />}
            </button>
            <button
              data-testid="nav-transpose"
              onClick={() => goTo("transpose")}
              className={`font-display font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                view === "transpose" ? "bg-foreground text-background" : "text-foreground hover:bg-background"
              }`}
            >
              <KeyRound className="w-4 h-4" /> {t("nav.transpose")} {!isPro && <Lock className="w-3 h-3 opacity-60" />}
            </button>
            <button
              data-testid="nav-circle"
              onClick={() => goTo("circle")}
              className={`font-display font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                view === "circle" ? "bg-foreground text-background" : "text-foreground hover:bg-background"
              }`}
            >
              <CircleDot className="w-4 h-4" /> {t("nav.circle")} {!isPro && <Lock className="w-3 h-3 opacity-60" />}
            </button>
            <button
              data-testid="nav-advanced"
              onClick={() => goTo("advanced")}
              className={`font-display font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                view === "advanced" ? "bg-foreground text-background" : "text-foreground hover:bg-background"
              }`}
            >
              <Sparkles className="w-4 h-4" /> {t("nav.advanced")} {!isPro && <Lock className="w-3 h-3 opacity-60" />}
            </button>
            <button
              data-testid="nav-quiz"
              onClick={() => goTo("quiz")}
              className={`font-display font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                view === "quiz" ? "bg-foreground text-background" : "text-foreground hover:bg-background"
              }`}
            >
              <HelpCircle className="w-4 h-4" /> {t("nav.quiz")} {!isPro && <Lock className="w-3 h-3 opacity-60" />}
            </button>
          </nav>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-16 md:pt-20 md:pb-24">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div className="float-in">
            <div className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground brutal-border rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-4 h-4" />
              <span className="step-tag">{t("hero.badge")}</span>
            </div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="hero-start-btn"
                onClick={scrollToPiano}
                className="font-display font-bold bg-secondary text-secondary-foreground px-6 py-3.5 rounded-full brutal-border brutal-shadow hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none transition-all flex items-center gap-2"
              >
                <Play className="w-4 h-4" /> {t("hero.tryNow")}
              </button>
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-medium px-2">
                <KeyRound className="w-4 h-4" /> {t("hero.noSheet")}
              </div>
            </div>
          </div>

          <div className="relative float-in" style={{ animationDelay: "0.12s" }}>
            <div className="absolute -inset-3 bg-foreground rounded-2xl rotate-2" />
            <img
              src={HERO_IMG}
              alt="A person smiling while playing the piano"
              className="relative rounded-2xl brutal-border w-full h-[340px] md:h-[420px] object-cover"
            />
            <div className="absolute -bottom-5 -left-5 bg-secondary text-secondary-foreground brutal-border brutal-shadow rounded-xl px-4 py-3 rotate-[-3deg]">
              <p className="font-display font-extrabold text-2xl leading-none">{t("hero.modesBadge")}</p>
              <p className="text-xs font-semibold">{t("hero.setBadge")}</p>
            </div>
          </div>
        </div>

        <button
          onClick={scrollToPiano}
          className="mt-16 mx-auto flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          data-testid="scroll-down"
        >
          {t("hero.seeHow")} <ArrowDown className="w-4 h-4 animate-bounce" />
        </button>
      </section>

      {/* ===== Interactive learning: MODES ===== */}
      {view === "modes" && (
      <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              {t("modes.h2")}
            </h2>
            <p className="mt-4 text-base text-background/70 leading-relaxed">
              {t("modes.intro")}
            </p>
          </div>

          {/* Mode selector */}
          <div
            className="mt-8 flex flex-wrap gap-2.5"
            role="tablist"
            data-testid="mode-selector"
          >
            {MODES.map((m) => {
              const isActive = m.id === activeId;
              return (
                <button
                  key={m.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`mode-selector-${m.id}`}
                  onClick={() => setActiveId(m.id)}
                  className={`font-display font-bold text-sm px-4 py-2.5 rounded-full border-2 transition-all ${
                    isActive
                      ? "bg-secondary text-secondary-foreground border-secondary -translate-y-0.5"
                      : "bg-transparent text-background/80 border-background/30 hover:border-background hover:-translate-y-0.5"
                  }`}
                  style={isActive ? { boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" } : {}}
                >
                  {modeName(m.name, lang)}
                  <span className="ml-1.5 opacity-60 font-semibold">{m.rootName}</span>
                </button>
              );
            })}
          </div>

          {/* Piano */}
          <div className="mt-8">
            <Piano activeMode={active} playingMidi={playingMidi} />
          </div>

          {/* Play scale + legend */}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              data-testid="play-scale-btn"
              onClick={handlePlayScale}
              className="font-display font-bold bg-secondary text-secondary-foreground px-5 py-3 rounded-full brutal-border hover:-translate-y-0.5 transition-transform flex items-center gap-2"
              style={{ boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" }}
            >
              <Play className="w-4 h-4" /> {t("modes.hearScale", { name: modeName(active.name, lang) })}
            </button>
            <div className="flex items-center gap-4 text-sm text-background/70">
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded bg-primary inline-block" /> {t("common.startNote")}
              </span>
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded inline-block" style={{ background: "#66AEFF" }} />{" "}
                {t("common.scaleNote")}
              </span>
            </div>
          </div>

          {/* Educational panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="mt-10 grid md:grid-cols-3 gap-5"
              data-testid="education-panel"
            >
              <div
                className="md:col-span-2 bg-background text-foreground brutal-border rounded-2xl p-7"
                style={{ boxShadow: "6px 6px 0 0 rgba(253,251,247,0.9)" }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="w-3 h-8 rounded"
                    style={{ background: active.color }}
                  />
                  <h3 className="font-display font-extrabold text-2xl tracking-tight">
                    {modeName(active.name, lang)}
                  </h3>
                  <span className="text-sm font-semibold text-muted-foreground">
                    · {t(`modeData.${active.id}.alias`)}
                  </span>
                </div>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t(`modeData.${active.id}.description`)}
                </p>

                <div className="mt-6">
                  <p className="step-tag text-muted-foreground mb-2">
                    {t("common.theNotes")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {active.scale.map((m, i) => (
                      <span
                        key={m}
                        className={`font-display font-bold text-sm px-3 py-1.5 rounded-lg brutal-border ${
                          i === 0 ? "bg-primary text-primary-foreground" : "bg-muted"
                        }`}
                      >
                        {noteLabel(noteName(m).replace(/[0-9]/g, ""), lang)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-secondary text-secondary-foreground brutal-border rounded-2xl p-7">
                <Music4 className="w-6 h-6 mb-3" />
                <p className="step-tag opacity-80 mb-1">{t("modes.mood")}</p>
                <p className="font-display font-bold text-lg mb-5">{t(`modeData.${active.id}.mood`)}</p>
                <p className="step-tag opacity-80 mb-2">{t("modes.hearIn")}</p>
                <ul className="space-y-2">
                  {active.songs.map((s) => (
                    <li key={s} className="text-sm font-medium flex gap-2">
                      <span className="opacity-60">♪</span> {s}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
      )}

      {/* ===== Interactive learning: CHORDS ===== */}
      {view === "chords" && (
      <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              {t("chords.h2")}
            </h2>
            <p className="mt-4 text-base text-background/70 leading-relaxed">
              {t("chords.intro")}
            </p>
          </div>

          {/* Chord selector */}
          <div
            className="mt-8 flex flex-wrap gap-2.5"
            role="tablist"
            data-testid="chord-selector"
          >
            {TRIADS.map((c, ci) => {
              const isActive = c.id === chordId;
              return (
                <button
                  key={c.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`chord-selector-${c.id}`}
                  onClick={() => setChordId(c.id)}
                  className={`font-display font-bold text-sm px-4 py-2.5 rounded-full border-2 transition-all ${
                    isActive
                      ? "bg-accent text-foreground border-accent -translate-y-0.5"
                      : "bg-transparent text-background/80 border-background/30 hover:border-background hover:-translate-y-0.5"
                  }`}
                  style={isActive ? { boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" } : {}}
                >
                  {chordLabel(c.name, lang)}
                  <span className="ml-1.5 opacity-70 font-semibold">{modeName(DIATONIC_MODES[ci], lang)}</span>
                  <span className="ml-1.5 opacity-60 font-semibold">{c.numeral}</span>
                </button>
              );
            })}
          </div>

          {/* Piano */}
          <div className="mt-8">
            <Piano activeMode={chordAsMode} playingMidi={playingMidi} />
          </div>

          {/* Play chord + legend */}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              data-testid="play-chord-btn"
              onClick={handlePlayChord}
              className="font-display font-bold bg-accent text-foreground px-5 py-3 rounded-full brutal-border hover:-translate-y-0.5 transition-transform flex items-center gap-2"
              style={{ boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" }}
            >
              <Play className="w-4 h-4" /> {t("chords.hearChord", { name: chordLabel(chord.name, lang) })}
            </button>
            <div className="flex items-center gap-4 text-sm text-background/70">
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded bg-primary inline-block" /> {t("common.startNote")}
              </span>
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded inline-block" style={{ background: "#66AEFF" }} />{" "}
                {t("common.chordNote")}
              </span>
            </div>
          </div>

          {/* Chord panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={chord.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="mt-10 grid md:grid-cols-3 gap-5"
              data-testid="chord-panel"
            >
              <div
                className="md:col-span-2 bg-background text-foreground brutal-border rounded-2xl p-7"
                style={{ boxShadow: "6px 6px 0 0 rgba(253,251,247,0.9)" }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-3 h-8 rounded" style={{ background: chord.color }} />
                  <h3 className="font-display font-extrabold text-2xl tracking-tight">
                    {chordLabel(chord.name, lang)}
                  </h3>
                  <span className="text-sm font-semibold text-muted-foreground">
                    · {quality(chord.quality, lang)} · {chord.numeral}
                  </span>
                </div>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {t(`triadData.${chord.id}`)}
                </p>
                <div className="mt-6">
                  <p className="step-tag text-muted-foreground mb-2">{t("chords.play3")}</p>
                  <div className="flex flex-wrap gap-2">
                    {chord.notes.map((m, i) => (
                      <span
                        key={m}
                        className={`font-display font-bold text-sm px-3 py-1.5 rounded-lg brutal-border ${
                          i === 0 ? "bg-primary text-primary-foreground" : "bg-muted"
                        }`}
                      >
                        {noteLabel(noteName(m).replace(/[0-9]/g, ""), lang)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-secondary text-secondary-foreground brutal-border rounded-2xl p-7">
                <ListMusic className="w-6 h-6 mb-3" />
                <p className="step-tag opacity-80 mb-2">{t("chords.whiteOnly")}</p>
                <p className="text-sm font-medium leading-relaxed">
                  {t("chords.whiteOnlyText")}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Relative minors */}
          <div className="mt-12" data-testid="relative-minors">
            <h3 className="font-display font-extrabold text-2xl tracking-tight mb-1">
              {t("chords.relH")}
            </h3>
            {t("chords.relText") && (
              <p className="text-background/70 text-sm mb-6 max-w-2xl">
                {t("chords.relText")}
              </p>
            )}
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                { major: "C Major", minor: "A minor", modes: ["Ionian", "Aeolian"] },
                { major: "G Major", minor: "E minor", modes: ["Mixolydian", "Phrygian"] },
                { major: "F Major", minor: "D minor", modes: ["Lydian", "Dorian"] },
              ].map((p, pi) => (
                <div
                  key={p.major}
                  className="bg-background text-foreground brutal-border rounded-2xl p-6"
                  style={{ boxShadow: "5px 5px 0 0 rgba(253,251,247,0.9)" }}
                >
                  <div className="flex items-center gap-2 font-display font-extrabold text-lg">
                    <span>{chordLabel(p.major, lang)}</span>
                    <Repeat className="w-4 h-4 text-primary" />
                    <span>{chordLabel(p.minor, lang)}</span>
                  </div>
                  <p className="text-xs font-semibold text-primary mt-1">
                    ({modeName(p.modes[0], lang)} - {modeName(p.modes[1], lang)})
                  </p>
                  <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{t("chords.relPairs")[pi]}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Progressions */}
          <div className="mt-12">
            <h3 className="font-display font-extrabold text-2xl tracking-tight mb-1">
              {t("chords.progH")}
            </h3>
            <p className="text-background/70 text-sm mb-6">
              {t("chords.progText")}
            </p>
            <div className="grid md:grid-cols-3 gap-5" data-testid="progressions">
              {PROGRESSIONS.map((p) => (
                <div
                  key={p.name}
                  className="bg-background text-foreground brutal-border rounded-2xl p-6"
                  style={{ boxShadow: "5px 5px 0 0 rgba(253,251,247,0.9)" }}
                >
                  <p className="font-display font-extrabold text-lg">{progName(p.name, lang)}</p>
                  <p className="step-tag text-primary mt-1">{p.numerals}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {p.chords.map((c) => (
                      <span
                        key={c}
                        className="text-xs font-bold bg-muted brutal-border rounded-md px-2 py-1"
                      >
                        {chordLabel(c, lang)}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                    {t("chords.usedIn")} {p.songs}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ===== Paywall (locked views, not Pro) ===== */}
      {LOCKED_VIEWS.includes(view) && !isPro && (
        <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
          <Paywall onUnlock={startCheckout} loadingKey={loadingKey} />
        </section>
      )}

      {/* ===== Guided practice (1st — key of C) ===== */}
      {view === "practice" && isPro && (
        <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
          <PracticeView routines={routinesC} intro={t("practice.intro")} heading={t("practice.playC")} />
        </section>
      )}

      {/* ===== Guided practice (2nd — key of G) ===== */}
      {view === "practice2" && isPro && (
        <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
          <PracticeView routines={routinesG} intro={t("practice.intro2")} heading={t("practice.playG")} />
        </section>
      )}

      {/* ===== Transpose ===== */}
      {view === "transpose" && isPro && (
      <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              {t("transpose.h2")} <span className="text-accent">{t("transpose.any")}</span>
            </h2>
            <p className="mt-4 text-base text-background/70 leading-relaxed">
              {t("transpose.intro")}
            </p>
          </div>

          <KeyExplorer keys={KEYS} testPrefix="key" />
        </div>
      </section>
      )}

      {/* ===== Advanced Keys ===== */}
      {view === "advanced" && isPro && (
      <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-8">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              {t("advanced.h2")} <span className="text-accent">{t("advanced.sub")}</span>
            </h2>
            <p className="mt-4 text-base text-background/70 leading-relaxed">
              {t("advanced.intro")}
            </p>
          </div>
          <KeyExplorer keys={ADVANCED_KEYS} testPrefix="adv" />
        </div>
      </section>
      )}

      {/* ===== Circle of Fifths ===== */}
      {view === "circle" && isPro && (
      <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-10">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              {t("circle.h2a")} <span className="text-accent">{t("circle.name")}</span> {t("circle.h2b")}
            </h2>
            <p className="mt-4 text-base text-background/70 leading-relaxed">
              {t("circle.intro")}
            </p>
          </div>
          <CircleOfFifths />
        </div>
      </section>
      )}

      {/* ===== Quiz ===== */}
      {view === "quiz" && isPro && (
      <section id="learn" className="bg-foreground text-background pt-6 pb-16 md:pt-8 md:pb-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-10">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              {t("quiz.h2")}
            </h2>
            <p className="mt-4 text-base text-background/70 leading-relaxed">
              {t("quiz.intro")}
            </p>
          </div>
          <Quiz />
        </div>
      </section>
      )}

      {/* ===== Why it works ===== */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight max-w-2xl">
          {t("why.h2")}
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { n: "01", t: t("why.c1t"), d: t("why.c1d"), c: "#66AEFF" },
            { n: "02", t: t("why.c2t"), d: t("why.c2d"), c: "#66AEFF" },
            { n: "03", t: t("why.c3t"), d: t("why.c3d"), c: "#66AEFF" },
          ].map((s) => (
            <div
              key={s.n}
              className="bg-card brutal-border brutal-shadow rounded-2xl p-7 hover:-translate-y-1 transition-transform"
            >
              <span
                className="font-display font-extrabold text-4xl"
                style={{ color: s.c }}
              >
                {s.n}
              </span>
              <h3 className="font-display font-bold text-xl mt-3 mb-2">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 bg-accent text-foreground brutal-border brutal-shadow-lg rounded-2xl p-8 md:p-12">
          <p className="font-display font-extrabold text-2xl sm:text-3xl leading-snug max-w-3xl">
            {t("why.quote")}
          </p>
          <button
            data-testid="footer-cta"
            onClick={scrollToPiano}
            className="mt-7 font-display font-bold bg-background text-foreground px-6 py-3.5 rounded-full hover:-translate-y-0.5 transition-transform inline-flex items-center gap-2"
          >
            <Play className="w-4 h-4" /> {t("why.back")}
          </button>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t-2 border-foreground py-8">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2" data-testid="footer-logo">
            <img src="/ionara-logo.png" alt="Ionara" className="h-7 w-auto" />
          </div>
          <div className="flex items-center gap-5">
            <button
              data-testid="about-btn"
              onClick={() => setAboutOpen(true)}
              className="text-sm font-display font-bold hover:text-primary transition-colors"
            >
              {t("footer.about")}
            </button>
            <p className="text-sm text-muted-foreground">
              {t("footer.tagline")}
            </p>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-4 text-sm text-muted-foreground">
          {t("footer.contact")}{" "}
          <a
            href="mailto:erikenzo@gmail.com"
            data-testid="footer-contact-email"
            className="font-semibold text-foreground hover:text-primary transition-colors underline"
          >
            erikenzo@gmail.com
          </a>
        </div>
      </footer>
      <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
        <DialogContent className="max-w-lg brutal-border max-h-[85vh] overflow-y-auto" data-testid="about-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl tracking-tight">
              {t("about.title")}
            </DialogTitle>
            <DialogDescription className="sr-only">{t("footer.tagline")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>{t("about.p1")}</p>
            <p>{t("about.p2")}</p>
            <p className="text-foreground font-display font-bold">
              {t("about.sign")}
            </p>

            <div className="pt-2 border-t-2 border-foreground/10">
              <img
                src="/erik-enzo.jpg"
                alt="Erik Enzo"
                data-testid="about-photo"
                className="float-right ml-4 mb-2 mt-3 w-28 sm:w-32 rounded-lg brutal-border object-cover"
              />
              <p className="font-display font-extrabold text-foreground tracking-wide mt-3">
                ERIK ENZO — BIO
              </p>
              <ul className="mt-3 space-y-2.5 text-sm">
                <li className="flex gap-2">
                  <span className="text-primary font-bold">·</span>
                  <span>CEO and founder of the music platform <strong className="text-foreground">MelodyPipe</strong>.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary font-bold">·</span>
                  <span>Lead singer in the awarded Norwegian band <strong className="text-foreground">“elle melle”</strong>, with 9 albums released and altogether almost 20 million plays on streaming platforms.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary font-bold">·</span>
                  <span>Has co-written songs with songwriters such as <strong className="text-foreground">David Lasley</strong> (Bonnie Raitt, Whitney Houston, Dionne Warwick, Tina Turner, Aretha Franklin, Natalie Cole…), <strong className="text-foreground">Allan Rich</strong> (Whitney Houston, Rod Stewart, N’Sync, Barbra Streisand…), <strong className="text-foreground">Lou Pardini</strong> (Chicago, Smokey Robinson, Michael McDonald…), and <strong className="text-foreground">Chris Barron</strong> (Spin Doctors).</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary font-bold">·</span>
                  <span>Has released two albums with his duo <strong className="text-foreground">“The Norwegian Fords” (TNF)</strong>, partly recorded and fully mixed by double Grammy-winner <strong className="text-foreground">Bill Schnee</strong>, whose clients include Whitney Houston, Miles Davis, Steely Dan, Billy Joel, Ringo Starr and Dire Straits.</span>
                </li>
              </ul>
              <p className="mt-4 text-sm">
                Contact:{" "}
                <a
                  href="mailto:erikenzo@gmail.com"
                  data-testid="about-email"
                  className="text-primary font-bold underline underline-offset-2"
                >
                  erikenzo@gmail.com
                </a>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AuthModal
        open={authModal.open}
        mode={authModal.mode}
        sessionId={authModal.sessionId}
        onOpenChange={(o) => setAuthModal((s) => ({ ...s, open: o }))}
        onSuccess={() => {
          if (authModal.mode === "postpay") setPayState("success");
        }}
      />
      <ResetPasswordModal
        open={!!resetToken}
        token={resetToken}
        onOpenChange={(o) => {
          if (!o) setResetToken(null);
        }}
        onDone={() => {
          setResetToken(null);
          setAuthModal({ open: true, mode: "login", sessionId: null });
        }}
      />
    </div>
  );
}

export default App;
